import React, { useState, useContext, useRef, useEffect } from 'react';
import { Modal } from 'bootstrap';
import { AppDataContext } from "../AppDataContext";
import useNetwork from '../../useNetwork';
import CurrencyFormat from 'react-currency-format';
import CurrencyInput from 'react-currency-input-field';
import { CountdownTimer } from './CountdownTimer';
import LoginAndRegistration from '../LoginAndRegistration';

export function BidDialog (props) {
	const context = useContext(AppDataContext);
	const { loading, netRequest } = useNetwork(false);

    const [isThisUser, setIsThisUser] = useState(props.lot.currentBid?.isThisUser);
	const [currentAmount, setCurrentAmount] = useState(props.lot.currentBid ? props.lot.currentBid.currentAmount : props.lot.startPrice);
	const [currentMaxAmount, setCurrentMaxAmount] = useState(props.lot.currentBid?.currentMaxAmount);

	const calcMinimumBid = (lot) => {
		let minimumBid = lot.startPrice;
		if (lot.currentBid) {
			minimumBid = lot.currentBid.currentAmount + lot.currentBid.incrementAmount;
			// if this user's current max IS what would be computed as the current minimum,
			// then require them to go up, yet again, to make a new bid
			if (lot.currentBid.currentMaxAmount == minimumBid) {
				minimumBid = minimumBid + lot.currentBid.incrementAmount;
			}
		}
		return minimumBid;
	}

	const [minimumBid, setMinimumBid] = useState(calcMinimumBid(props.lot));
	const [confirmBidAmount, setConfirmBidAmount] = useState(props.bidAmount);
	const [numberOfBids, setNumberOfBids] = useState(props.lot.bidCount);
	const [postBidResult, setPostBidResult] = useState(null);
	const [inputValue, setInputValue] = useState('');
	const [wasValidated, setWasValidated] = useState(false);
    const [showConfirm, setShowConfirm] = useState(props.showConfirm ? true : false);
	const [bidResultMessage, _setBidResultMessage] = useState(null);
	const setBidResultMessage = (message, isError) => {
		if (message == null)
			_setBidResultMessage(null);
		else
			_setBidResultMessage({ message: message, isError: isError });
	};
	const [isInAuction, setIsInAuction] = useState(false);
	const [lot, setLot] = useState(props.lot);

	useEffect(() => {
		// only update when the lot has really changed, as opposed to a refresh of the same lot we opened from
		// which can happen when the lot list on the base page is refreshed when the bid dialog is open
		if (lot.secureId != props.lot.secureId) {
			refreshDialog();
		}
	}, [props.lot]);

	useEffect(() => {
		setIsInAuction(context.isRegisteredForAuction(props.lot.auctionSession.auction.secureId));
	}, [context.registeredAuctionsChanged]);

	useEffect(() => {
		refreshDialog();
	}, [props.reinitialize]);

	const refreshDialog = () => {
		setIsThisUser(props.lot.currentBid?.isThisUser);
		setCurrentMaxAmount(props.lot.currentBid?.currentMaxAmount);
		setCurrentAmount(props.lot.currentBid ? props.lot.currentBid.currentAmount : props.lot.startPrice);
		setMinimumBid(calcMinimumBid(props.lot));
		setConfirmBidAmount(props.bidAmount);
		setNumberOfBids(props.lot.currentBid ? props.lot.currentBid.bidCount : 0);
		setPostBidResult(null);
		setInputValue('');
		setWasValidated(false);
		setShowConfirm(props.showConfirm ? true : false);
		setBidResultMessage(null);
		setIsInAuction(context.isRegisteredForAuction(props.lot.auctionSession.auction.secureId));
		setLot(props.lot);
	};

	const bidInputRef = useRef();

	const onSubmitBidForm = async (event) => {
		event.preventDefault();
		await showConfirmClicked();
	};

	const showConfirmClicked = async () => {
		setWasValidated(true);

		if (inputValue < minimumBid) {
			bidInputRef.current.setCustomValidity('invalid');
		} else {
			bidInputRef.current.setCustomValidity('');
			if (inputValue != '' && inputValue >= minimumBid) {
				setBidResultMessage(null);
				setConfirmBidAmount(inputValue);
				setShowConfirm(true);
			}
		}
    }


	const updateBidValue = (value) => {
		if (!value) {
			setInputValue('');
		} else {
			setInputValue(value);
		}
		bidInputRef.current.setCustomValidity('');
	}

	const backClicked = () => {
		setWasValidated(false);
		setShowConfirm(false);
		setPostBidResult(null);
	}


	const onSubmitConfirmForm = async (event) => {
		event.preventDefault();
		await placeBid();
	};

	const placeBid = async () => {
		setPostBidResult(null);

		const url = 'api/site/placebid';

		// create a unique id for this bid, which will be returned on the broadcast
		// this way we can ignore that particular broadcast, since we will have already updated
		// our component with this bid result
		const bidUniqueId = Math.random().toString();
		context.bidUniqueIds.push(bidUniqueId);
		const postData = {
			lotSecureId: props.lot.secureId,
			bidAmount: confirmBidAmount,
			bidUniqueId: bidUniqueId,
		};

		const response = await netRequest(url, postData);

		if (response.data != null) {
			setPostBidResult(response.data.bidResultEnum);

			switch (response.data.bidResultEnum) {
				case "NotApproved":
					setBidResultMessage("Your bid had been entered, but is now Pending approval of your registration to this Auction", false);
					props.onBidPlaced(response.data, false);
					break;

				// these should not happen, as the edit field should not allow it
				case "FailedMinimum":
				case "IllegalIncrement":
					setBidResultMessage("Too Low – your bid was lower than the current bid asking price.", true);
					break;

				case "LotClosed":
					setBidResultMessage("Lot Closed – Sorry, your bid was submitted after the lot closed.", true);
					break;

				case "LotWidthdrawn":
					setBidResultMessage("Withdrawn – Sorry, the auctioneer has withdrawn this lot.", true);
					break;

				case "SelfUnderbid":
				case "FailedLowBid":
				case "BidOutbidByMax":
				case "BidWins":
				case "DecreaseMax":
				case "IncreaseMax":
					switch (response.data.bidResultEnum) {
						case "SelfUnderbid":
							setBidResultMessage("Error – your bid did not post because it was lower than a previous bid placed by you.", true);
							break;
						case "FailedLowBid":
							setBidResultMessage("Too Low – your bid was lower than the current bid asking price.", true);
							break;
						case "BidOutbidByMax":
						case "BidWins":
						case "DecreaseMax":
						case "IncreaseMax":
							const formatter = new Intl.NumberFormat('en-US', {
								style: 'currency',
								currency: 'USD',
							});
							switch (response.data.bidResultEnum) {
								case "BidOutbidByMax":
									setBidResultMessage("Outbid - Your bid has posted successfully, but it is lower than another bidder’s maximum bid.", true);
									break;
								case "BidWins":
									setBidResultMessage("You're the High Bidder! – You have the high bid at " + formatter.format(response.data.currentAmount), false);
									break;
								case "DecreaseMax":
									setBidResultMessage("Bid Decreased – your maximum bid has been reduced to " + formatter.format(response.data.currentMaxAmount), false);
									break;
								case "IncreaseMax":
									setBidResultMessage("Bid Increased – your maximum bid has been increased to " + formatter.format(response.data.currentMaxAmount), false);
									break;
							}
							props.onBidPlaced(response.data, false);
							break;
					}

					// update values for the new bid
					setCurrentAmount(response.data.currentAmount);
					setInputValue('');
					setMinimumBid(response.data.currentAmount + response.data.incrementAmount);
					setNumberOfBids(response.data.bidCount);
					break;
					// all good, close the dialog
					props.onBidPlaced(response.data, false);
					Modal.getInstance("#" + props.id).hide();
					break;
			}
		}
	}

	return (
        <>
            <div class="modal fade" id={props.id} tabindex="-1" aria-labelledby={props.id + "Label"} aria-hidden="true">
				<div className={"modal-dialog modal-dialog-centered" +
					((!context.isLoggedIn || !context.emailVerified || !context.hasPayment || isInAuction === false) ? " modal-lg" : "")
				}					>
					<div class="modal-content">
						<div class="modal-header">
							<img src={"api/site/SellerLogo/" + props.lot.auctionSession.auction.companySecureId} alt={props.lot.auctionSession.auction.companyName}
								title={props.lot.auctionSession.auction.companyName} />
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
                        <div class="modal-body">
							{(!context.emailVerified || !context.hasPayment || isInAuction === false) &&
								<LoginAndRegistration
									showLogin={true}
									showSignup={true}
									showReadOnlyTerms={false}
									auctionSession={props.lot.auctionSession}
								/>
                            }


                            {
								context.emailVerified &&
								context.hasPayment &&
								isInAuction &&
                                !showConfirm &&

								<div>
									<div class="row">
										<div class="col-12">
                                            <h3>{props.lot.title}</h3>
											<div class="divider mb-3"></div>
										</div>

										<div class="col-4 col-md-4 mb-3">
											<img src={'api/site/InventoryImage/' + props.lot.inventoryItemImageSecureId + '/450'} title="Image" alt="Image" class="w-100" />
										</div>
										<div class="col-8 col-md-8 mb-3 place-bid">
											<span>Current Bid</span>
												<h4 class="price display-1"><CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={currentAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} /></h4>
												{props.lot.auctionSession != null && props.lot.auctionSession.auction.isRegistered && props.lot.auctionSession.auction.paddleNumber == null ? (
													<>
														<label class="d-inline me-1 mb-3 auction-label pending">Pending Approval</label>
														{
															props.lot.pendingBidAmount != null &&
															<label class="d-inline me-1 mb-3 auction-label pending">Your Max
																&nbsp;
																	<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={props.lot.pendingBidAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
															</label>
														}
													</>
												) : (
												<>
													{currentMaxAmount &&
														<>
														<label class="me-1 auction-label highest">High Bid</label>
															&nbsp;
															<label class="d-inline me-1 mb-3 auction-label pending">Your Max
																&nbsp;
																	<CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={currentMaxAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
															</label>
														</>
													}

												</>
												)}

												{!isThisUser && props.lot.hasBid &&
													<label class="me-1 auction-label outbidded">Outbid</label>
												}

												<CountdownTimer secondsToClose={props.secondsToClose} />
										</div>
									</div>

									<form className={"my-3 lot-details__div--bid needs-validation"} onSubmit={onSubmitBidForm}>
										<div class="row">
											<div class="col mb-3">
													<div className={"input-group input-group__inline has-validation " +(wasValidated ? 'was-validated' :'') }>                                                        
														<CurrencyInput
															required class="form-control" placeholder="Bid"
															onValueChange={(value, name) => updateBidValue(value)}
															decimalsLimit={2}
															decimalScale={2}
															allowDecimals={true}
															allowNegativeValue={false}
															prefix="$"
															value={inputValue}
															ref={bidInputRef}
														/>
														<div class="input-group-append">
															<button type="button" onClick={showConfirmClicked} class="btn btn-primary btn-fill w-100">
																{isThisUser === true ? "Update Max" : "Place Bid"}
															</button>
														</div>
														<div class="invalid-feedback">
															Please bid equal to or greater than the minimum.
														</div>
												</div>
												<div class="d-flex justify-content-between">
														<label>Minimum bid
															&nbsp;
															<CurrencyFormat
																decimalScale={2}
																fixedDecimalScale={true}
																value={minimumBid}
																displayType={'text'}
																thousandSeparator={true}
																prefix={'$'} />
														</label>
													{numberOfBids} bid{numberOfBids != 1 && "s"}
												</div>
											</div>
												
											<label><i class="bi bi-lock-fill"></i> Secure bid</label>

										</div>
									</form>
								</div>
							}

							{
								context.emailVerified &&
								context.hasPayment &&
								isInAuction &&
								showConfirm &&

								<div>
									{postBidResult == null && <h5 class="text-center">You are about to place this bid</h5>}

									<div class="row">
										<div class="col-12">
											<h3>{props.lot.title}</h3>
											<div class="divider mb-3"></div>
										</div>
											{bidResultMessage != null &&
												<div class="col-12">
												<div className={"text-center alert " + (bidResultMessage.isError ? "alert-danger" : "alert-success")}>
													{bidResultMessage.message}
													</div>
												</div>
											}
											{bidResultMessage == null &&
												<>
													<div class="col-4 col-md-4 mb-3">
														<img src={'api/site/InventoryImage/' + props.lot.inventoryItemImageSecureId + '/450'} title="Image" alt="Image" class="w-100" />
													</div>
													<div class="col-8 col-md-8 mb-3 place-bid">
														<h4 class="price display-1"><CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={confirmBidAmount} displayType={'text'} thousandSeparator={true} prefix={'$'} />
														</h4>
														<p class="mb-0 pb-0">
															+ <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={confirmBidAmount * props.lot.auctionSession.auction.buyersPremium} displayType={'text'} thousandSeparator={true} prefix={'$'} />
															&nbsp;
															(<CurrencyFormat decimalScale={0} value={100 * props.lot.auctionSession.auction.buyersPremium} displayType={'text'} suffix={'%'} /> Buyer Premium)
														</p>
														{/*
												// Alex does not want to compute and add in sales tax.  So commenting out for now,
												// and placing in a Total without but leaving it for the time being, in case he changes his mind
												{props.lot.auctionSession.auction.salesTax && 
													<p class="mb-0 pb-0">
														{context.taxExemptTaxDocSecureId == null &&
															<>+ <CurrencyFormat decimalScale={2} fixedDecimalScale={true} value={confirmBidAmount * props.lot.auctionSession.auction.salesTax} displayType={'text'} thousandSeparator={true} prefix={'$'} />
															</>
														}
														&nbsp;
														{context.taxExemptTaxDocSecureId == null ?
															<>(<CurrencyFormat decimalScale={2} value={100 * props.lot.auctionSession.auction.salesTax} displayType={'text'} suffix={'%'} /> Sales Tax)</>
															: <>(Exempt from Sales Tax)</>
														}
													</p>
												}
												<p class="mb-0 pb-0 total">Total <span>
														<CurrencyFormat
															decimalScale={2} fixedDecimalScale={true}
															value={confirmBidAmount * (1 + props.lot.auctionSession.auction.buyersPremium + ((context.taxExemptTaxDocSecureId == null ? 1 : 0) * props.lot.auctionSession.auction.salesTax ?? 0))}
															displayType={'text'} thousandSeparator={true} prefix={'$'} />
													</span>
												</p>
											*/}
														<p class="mb-0 pb-0 total">Total <span>
															<CurrencyFormat
																decimalScale={2} fixedDecimalScale={true}
																value={confirmBidAmount * (1 + props.lot.auctionSession.auction.buyersPremium)}
																displayType={'text'} thousandSeparator={true} prefix={'$'} />
														</span> plus applicable sales tax
														</p>
														<CountdownTimer secondsToClose={props.secondsToClose} />
													</div>
											</>
										}
										<form class="my-3 lot-details__div--bid needs-validation" noValidate onSubmit={onSubmitConfirmForm}>
											<div class="row">
												{postBidResult != "LotClosed" &&
													postBidResult != "LotWidthdrawn" &&
													postBidResult != "NotApproved" &&

													<div class="col mb-3">
													{
														props.showConfirm ? (
															<button type="button" class="btn btn-primary w-100" data-bs-dismiss="modal" aria-label="Close">
																{postBidResult == null ? "Cancel" : "Close"}
															</button>
															) : (
																	// per alex - changed to force the user to close window if they bid and get a message back.
																	postBidResult == null &&
																	<button type="button" onClick={backClicked} disabled={loading} class="btn btn-primary w-100">
																			{postBidResult == null ? "Back" : "Rebid"}
																				
																		</button> 
															)
																
														} 
													</div>
                                                }
												<div class="col mb-3">
													{
														// when a bid is successful, the dialog closes.  if it fails, there is a postBidResult
														// with an error message, and you can't bid again.  so don't show this button
														postBidResult == null ? (
															<button type="button" onClick={placeBid} disabled={loading} class="btn btn-secondary btn-fill w-100">
																	Confirm Bid
																</button>
														) : (
															<>
																{props.showConfirm == false &&
																	<button type="button" class="btn btn-primary w-100" data-bs-dismiss="modal" aria-label="Close">
																		Close
																	</button> 
																	}
																	
															</>
														)
													} 
												</div>
											</div>
										</form>

									</div>
								</div>
							}
						</div>
					</div>
				</div>
			</div>
		</>
    );
}
